// =============================================================================
// MeuDriver — NotificationBell (Batch R1)
// =============================================================================
// Bell icon com badge de não lidas + dropdown de notificações in-app.
// Usa a tabela `in_app_notifications` (distinta da legada `notifications`).
// =============================================================================

import { useRef, useState, useEffect } from 'react'
import { Bell, CheckCheck, Calendar, MessageCircle, Star, X, BookOpen, DollarSign, ArrowRight, Info } from 'lucide-react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { motion, AnimatePresence } from 'framer-motion'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import {
  useMyInAppNotifications,
  useUnreadInAppNotificationCount,
  useMarkInAppNotificationRead,
  useMarkAllInAppNotificationsRead,
} from '@/hooks/useInAppNotifications'
import type { InAppNotificationRow } from '@/types/database'

// ---------------------------------------------------------------------------
// Icon por tipo
// ---------------------------------------------------------------------------

function NotifIcon({ type }: { type: string }) {
  const base = 'h-4 w-4'
  switch (type) {
    case 'booking_confirmed':    return <Calendar      className={cn(base, 'text-brand-500')} />
    case 'booking_started':      return <BookOpen      className={cn(base, 'text-teal-500')} />
    case 'booking_completed':    return <CheckCheck    className={cn(base, 'text-green-500')} />
    case 'booking_cancelled':    return <X             className={cn(base, 'text-red-500')} />
    case 'new_message':          return <MessageCircle className={cn(base, 'text-brand-500')} />
    case 'new_review':           return <Star          className={cn(base, 'text-yellow-500')} />
    case 'payout_released':      return <DollarSign    className={cn(base, 'text-green-600')} />
    case 'system':               return <Info          className={cn(base, 'text-slate-400')} />
    default:                     return <Bell          className={cn(base, 'text-muted-foreground')} />
  }
}

// ---------------------------------------------------------------------------
// Item de notificação
// ---------------------------------------------------------------------------

interface NotifItemProps {
  n: InAppNotificationRow
  onClose: () => void
}

function NotifItem({ n, onClose }: NotifItemProps) {
  const markRead = useMarkInAppNotificationRead()
  const unread = !n.is_read
  const timeAgo = formatDistanceToNow(parseISO(n.created_at), {
    addSuffix: true,
    locale: ptBR,
  })

  function handleRead() {
    if (!unread) return
    markRead.mutate(n.id, {
      onError: () => toast.error('Erro ao marcar notificação como lida.'),
    })
  }

  const inner = (
    <div
      data-testid="notif-item"
      className={cn(
        'flex items-start gap-3 px-4 py-3 transition-colors',
        unread
          ? 'bg-brand-50/60 hover:bg-brand-50 cursor-pointer'
          : 'hover:bg-muted/40 cursor-default',
      )}
      onClick={handleRead}
    >
      <div className={cn(
        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
        unread ? 'bg-white shadow-sm border border-gray-100' : 'bg-muted',
      )}>
        <NotifIcon type={n.type} />
      </div>

      <div className="flex-1 min-w-0">
        <p className={cn(
          'text-sm leading-snug',
          unread ? 'font-semibold text-gray-900' : 'font-medium text-muted-foreground',
        )}>
          {n.title}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
        <p className="text-[11px] text-muted-foreground/70 mt-1">{timeAgo}</p>
      </div>

      {unread && (
        <div className="mt-2 h-2 w-2 shrink-0 rounded-full bg-brand-500" />
      )}
    </div>
  )

  // ALTO-001: sanitiza action_url para prevenir XSS armazenado (javascript: etc.)
  const safeUrl = (url: string | null): string | null => {
    if (!url) return null
    if (url.startsWith('/') || url.startsWith('https://') || url.startsWith('http://')) return url
    return null
  }

  const resolvedUrl = safeUrl(n.action_url)
  if (resolvedUrl) {
    return (
      <Link
        to={resolvedUrl}
        onClick={() => { handleRead(); onClose() }}
        className="block"
      >
        {inner}
      </Link>
    )
  }
  return <div>{inner}</div>
}

// ---------------------------------------------------------------------------
// NotificationBell
// ---------------------------------------------------------------------------

interface NotificationBellProps {
  /** Variante visual: 'light' = fundo claro (header), 'dark' = sidebar escura */
  variant?: 'light' | 'dark'
}

export function NotificationBell({ variant = 'light' }: NotificationBellProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const { data: notifications = [] } = useMyInAppNotifications()
  const unreadCount = useUnreadInAppNotificationCount()
  const markAll = useMarkAllInAppNotificationsRead()

  // Fecha ao clicar fora
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  // Fecha com Escape
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open])

  function handleMarkAll() {
    markAll.mutate(undefined, {
      onError: () => toast.error('Erro ao marcar todas como lidas.'),
    })
  }

  const bellClass = variant === 'dark'
    ? 'text-white/70 hover:text-white hover:bg-white/10'
    : ''

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="icon"
        className={cn('relative', bellClass)}
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notificações in-app${unreadCount > 0 ? ` (${unreadCount} não lidas)` : ''}`}
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5" />
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              key="badge"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 20 }}
              className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white leading-none"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </Button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute right-0 top-full mt-2 z-50 w-80 sm:w-96 rounded-2xl border border-gray-100 bg-white shadow-xl overflow-hidden"
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm text-gray-900">Notificações</h3>
                {unreadCount > 0 && (
                  <span className="inline-flex items-center justify-center rounded-full bg-brand-100 text-brand-700 text-[10px] font-bold px-1.5 py-0.5">
                    {unreadCount} nova{unreadCount > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAll}
                  disabled={markAll.isPending}
                  className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors disabled:opacity-50"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  Marcar todas como lidas
                </button>
              )}
            </div>

            {/* Lista */}
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 mb-3">
                  <Bell className="h-6 w-6 text-gray-300" />
                </div>
                <p className="text-sm font-medium text-gray-600">Nenhuma notificação</p>
                <p className="text-xs text-muted-foreground mt-1">Você está em dia!</p>
              </div>
            ) : (
              <ScrollArea className="max-h-[420px]">
                <div className="divide-y divide-gray-100">
                  {notifications.map((n) => (
                    <NotifItem
                      key={n.id}
                      n={n}
                      onClose={() => setOpen(false)}
                    />
                  ))}
                </div>
              </ScrollArea>
            )}

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="border-t border-gray-100 bg-gray-50 px-4 py-2.5 flex items-center justify-between">
                <p className="text-[11px] text-muted-foreground">
                  {notifications.length} notificações
                </p>
                <Link
                  to="/aluno/notificacoes/inbox"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700 transition-colors"
                >
                  Ver todas
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
